import { i18n } from "../../i18n/i18n";

import React, { Component, createRef } from 'react'
import {
  last, range, forEach, dropRight, size, omitBy, isNil, includes
} from 'lodash'
import { produce } from 'immer'
import workOrderApi, { WorkOrderData } from '../../apis/workOrder.api'
import packTraceApi, { PackTraceCreateRequest, PackTraceValidatePackCodeRequest, ManagerUpcRequest } from '../../apis/packTrace.api'
import workOrderService from '../../services/workOrder.service'
import storageService from '../../services/storage.service'
import alertService from '../../services/alert.service'
import geolocationService from '../../services/geolocation.service'
import confirmService from '../../services/confirm.service'
import inventoryCaseApi from '../../apis/inventoryCase.api'
import { PalletData } from '../../apis/pallet.api'
import metaApi from '../../apis/meta.api'
import { ConfirmParams } from '../../components/Confirm/Confirm.state'
import Button from '../../components/Button'
import Card from '../../components/Card'
import ContentLayout from '../../components/ContentLayout'
import ContentMain from '../../components/ContentMain'
import ItemCard from '../../components/ItemCard'
import Row from '../../components/Row/Row'
import SearchPicker, { SearchPickerOption } from '../../components/SearchPicker'
import Progress from '../../components/Progress'
import PackTraceCreateBundles from '../../partials/PackTraceCreateBundles'
import PackTraceCreateCase from '../../partials/PackTraceCreateCase'
import HeadingRow from '../../components/HeadingRow'
import InputText, { InputTextRef } from '../../components/InputText'
import InputScan from '../../components/InputScan'
import PalletCases from './PalletCases'
import Popup from '../../components/Popup/popup';
import authService from '../../services/auth.service'

interface PackTraceCreateProps {}

interface PackTraceCreateState {
  code: string
  lineNumber: string
  palletCode: string
  caseCode: string
  workOrder: WorkOrderData | null
  workOrderId: number | null
  palletMeta: boolean
  bundleCodes: string[]
  sessionPallet: PalletData | null
  bundleScannedPositions: number[]
  caseScannedCodes: string[]
  workOrders: WorkOrderData[]
  latitude: number | null
  longitude: number | null
  onScanError: boolean[]
  onCaseScanError: boolean
  autoFocusOnLPN?: boolean
  isPopupOpen : boolean
}

export default class PackTraceCreate
  extends Component<PackTraceCreateProps, PackTraceCreateState> {
  static inputRefs: InputTextRef[] = []
  resettableState = {
    code: '',
    lineNumber: '',
    palletCode: '',
    caseCode: '',
    workOrder: null,
    workOrderId: null,
    sessionPallet: null,
    bundleCodes: [],
    bundleScannedPositions: [],
    caseScannedCodes: [],
    workOrders: [],
    onScanError : [],
    onCaseScanError : false,
    autoFocusOnLPN: false,
    isPopupOpen : false
  }
  initialState: PackTraceCreateState = {
    code: '',
    lineNumber: '',
    palletCode: '',
    caseCode: '',
    workOrder: null,
    workOrderId: null,
    palletMeta: false,
    sessionPallet: null,
    bundleCodes: [],
    bundleScannedPositions: [],
    caseScannedCodes: [],
    workOrders: [],
    latitude: null,
    longitude: null,
    onScanError : [false,false,false,false,false,false,false],
    onCaseScanError : false,
    autoFocusOnLPN: false,
    isPopupOpen: false,
  }

  constructor (props: PackTraceCreateProps) {
    super(props)
    this.state = this.initialState
    this.handleInputChange = this.handleInputChange.bind(this)
    this.handleWorkOrderSearch = this.handleWorkOrderSearch.bind(this)
    this.handleWorkOrderSelect = this.handleWorkOrderSelect.bind(this)
    this.handleResetClick = this.handleResetClick.bind(this)
    this.handleStartNewSessionClick = this.handleStartNewSessionClick.bind(this)
    this.handleBundleScan = this.handleBundleScan.bind(this)
    this.handleCaseScan = this.handleCaseScan.bind(this)
    this.handleWorkOrderBlur = this.handleWorkOrderBlur.bind(this)
    this.onScanErrorCallback = this.onScanErrorCallback.bind(this)
    this.onCaseScanErrorCallback = this.onCaseScanErrorCallback.bind(this)
    this.handleLPNAutoFocusCallback = this.handleLPNAutoFocusCallback.bind(this)
    this.handleUPCValidationError = this.handleUPCValidationError.bind(this)
  }

  get palletCodeInput () {
    const { palletMeta, palletCode } = this.state
    return palletMeta && <InputScan
      name="palletCode"
      value={palletCode}
      label={i18n("Scan pallet code")}
      onChange={this.handleInputChange}
      onScan={this.handleInputScan}
      autofocus={this.state.autoFocusOnLPN}
      autoFocusCallback={this.handleLPNAutoFocusCallback} />
  }

  get progress () {
    const { workOrder } = this.state
    if (!workOrder || !workOrder.item) return null
    const caseScannedCount = size(this.state.caseScannedCodes)
    return (
      <Progress
        total={workOrder.item.cpp}
        completed={caseScannedCount} />
    )
  }

  get item () {
    const { workOrder, palletMeta } = this.state
    if (!workOrder || !workOrder.item) return null
    const { customerPartNumbers } = workOrder.item
    const partNumber = customerPartNumbers ? customerPartNumbers[0] : undefined
    const { qpc, cpb } = workOrder.item
    const ppp = qpc/cpb
    return <ItemCard
      code={workOrder.item.code}
      description={workOrder.item.description}
      cpp={workOrder.item.cpp}
      qpc={workOrder.item.qpc}
      upc={palletMeta ? partNumber : undefined}
      ppp={palletMeta ? ppp : undefined} />
  }

  get palletCases () {
    const { palletMeta, workOrder, caseScannedCodes } = this.state
    return palletMeta && workOrder && workOrder.item &&
      <PalletCases cpp={workOrder.item.cpp} scanned={caseScannedCodes} />
  }

  get caseRef () {
    return last(PackTraceCreate.inputRefs) as InputTextRef
  }

  get case () {
    const { workOrder } = this.state
    return workOrder &&
      <PackTraceCreateCase
        scan={this.state.caseCode}
        inputRef={this.caseRef}
        onScan={this.handleCaseScan}
        onCaseScanError={this.state.onCaseScanError}
        onCaseScanErrorCallback={this.onCaseScanErrorCallback} />
  }

  get workOrderOptions () {
    return workOrderService.searchOptions(this.state.workOrders)
  }

  get bundleRefs () {
    return dropRight(PackTraceCreate.inputRefs)
  }

  get canCreate () {
    const { workOrderId, bundleCodes, caseCode, workOrder } = this.state
    const hasFilledFields = workOrderId && caseCode && bundleCodes
    const bundlesCount = this.bundlesCount(workOrder)
    const bundleCodesCount = size(bundleCodes)
    const hasFilledAllBundles = bundleCodesCount === bundlesCount
    return hasFilledFields && hasFilledAllBundles
  }

  bundlesCount (workOrder: WorkOrderData | null) {
    if (!workOrder || !workOrder.item) return 0
    return workOrder.item.cpb || 0
  }

  totalBoxesCount (workOrder: WorkOrderData | null) {
    const bundlesCount = this.bundlesCount(workOrder)
    const caseCount = 1
    return bundlesCount + caseCount
  }

  createInputRefs (workOrder: WorkOrderData) {
    const totalBoxesCount = this.totalBoxesCount(workOrder)
    const positions = range(totalBoxesCount)
    forEach(positions, () => {
      const inputRef = createRef<HTMLInputElement>()
      PackTraceCreate.inputRefs.push(inputRef)
    })
  }

  focusNextBundle (position: number) {
    const nextBundleIndex = position
    const nextBundleRef = this.bundleRefs[nextBundleIndex]
    if (nextBundleRef && nextBundleRef.current) {
      nextBundleRef.current.focus()
    } else {
      const caseRef = this.caseRef
      if (caseRef && caseRef.current) caseRef.current.focus()
    }
  }

  focusFirstBundle () {
    this.focusNextBundle(0)
  }

  reset () {
    const { code, lineNumber, workOrder, workOrderId, palletCode } = this.state
    const { palletMeta, caseScannedCodes, sessionPallet } = this.state
    const rerenderBundleInputs = { workOrder: null }
    this.setState(rerenderBundleInputs, () => {
      const state = produce(this.initialState, state => {
        state.code = code
        state.lineNumber = lineNumber
        state.palletMeta = palletMeta
        state.palletCode = palletCode
        state.workOrder = workOrder
        state.workOrderId = workOrderId
        state.caseScannedCodes = caseScannedCodes
        state.sessionPallet = sessionPallet
      })
      this.setState(state, () => {
        this.focusFirstBundle()
      })
    })
  }

  dumpSession () {
    const authData = authService.tokenData()
    if (!authData) return
    const { id } = authData

    const { code, lineNumber, palletCode, workOrder, workOrderId, isPopupOpen } = this.state
    const { palletMeta, caseScannedCodes, sessionPallet } = this.state
    storageService.setWithId('packTraceSession', id.toString(), {
      code,
      palletMeta,
      lineNumber,
      palletCode,
      workOrder,
      workOrderId,
      caseScannedCodes,
      sessionPallet,
      isPopupOpen
    })
  }

  restoreSession () {
    const authData = authService.tokenData()
    if (!authData) return
    const { id } = authData

    const session = storageService.getWithId('packTraceSession', id.toString())
    if (!session) return
    const {
      code,
      palletMeta,
      lineNumber,
      palletCode,
      workOrder,
      workOrderId,
      caseScannedCodes,
      sessionPallet,
      isPopupOpen
    } = session
    const state = omitBy({
      code,
      palletMeta,
      lineNumber,
      palletCode,
      workOrder,
      workOrderId,
      caseScannedCodes,
      sessionPallet,
      isPopupOpen
    }, isNil) as PackTraceCreateState
    this.setState(state, () => {
      this.createInputRefs(workOrder)
      this.forceUpdate()
    })
  }

  resetSession () {
    const authData = authService.tokenData()
    if (!authData) return
    const { id } = authData

    storageService.removeWithId('packTraceSession', id.toString())
    this.setState(this.resettableState)
  }

  autoResetSession () {
    const { workOrder, caseScannedCodes } = this.state
    if (!workOrder || !workOrder.item) return
    if (size(caseScannedCodes) === workOrder.item.cpp) {
      const {
        code,
        palletMeta,
        lineNumber,
        palletCode,
        workOrder,
        workOrderId,
      } = this.state
      this.resetSession()
      this.setState({
        code,
        palletMeta,
        lineNumber,
        palletCode,
        workOrder,
        workOrderId,
      })
      this.setState({ isPopupOpen: false, palletCode: '', autoFocusOnLPN: true})
    }
  }

  readWorkOrder () {
    const { workOrderId } = this.state
    if (!workOrderId) return
    workOrderApi.read(workOrderId, workOrder => {
      if (!workOrder) return
      this.createInputRefs(workOrder)
      this.setState({ workOrder })
    })
  }

  pushCaseScannedCodes (caseCode: string) {
    const caseScannedCodes = produce(this.state.caseScannedCodes, codes => {
      codes.push(caseCode)
    })
    this.setState({ caseScannedCodes }, this.autoResetSession)
  }

  handleInputChange (name: string, value: string) {
    if (name === 'code') {
      this.setState({
        code: value,
        caseCode: '',
        workOrder: null,
        workOrderId: null,
        bundleCodes: [],
        bundleScannedPositions: []
      })
    } else if (name === 'lineNumber') {
      this.setState({ lineNumber: value })
    } else if (name === 'palletCode') {
      this.setState({ palletCode: value })
    }
  }

  handleInputScan = (name: string, value: string) => {
    if (name === 'palletCode') {
      this.setState({ palletCode: value })
      if(!value) return
      packTraceApi.validatePalletCode(value, response => {
        if(!response) return
        if(response.valid) this.focusNextBundle(0)
      })
    }
  }

  handleWorkOrderSearch (name: string, code: string) {
    workOrderApi.searchByCode(code, workOrders => {
      if (!workOrders) return
      this.setState({ workOrders })
    })
  }

  handleWorkOrderSelect (name: string, option: SearchPickerOption) {
    const workOrderId = option.id as number
    const state = { workOrderId, workOrders: [] }
    this.setState(state, this.readWorkOrder)
  }

  handleWorkOrderBlur () {
    this.setState({ workOrders: [] })
  }

  handleResetClick () {
    this.reset()
  }

  handleStartNewSessionConfirm () {
    this.resetSession()
  }

  handleStartNewSessionClick () {
    const confirm: ConfirmParams = {
      id: 1,
      type: 'error',
      question: i18n('This action will close the current pallet and start a new one. Do you want to continue?'),
      confirmText: i18n('Yes'),
      cancelText: i18n('No'),
      onConfirm: () => this.handleStartNewSessionConfirm()
    }
    confirmService.push(confirm)
  }

  onScanErrorCallback(){
    this.setState({ onScanError: [false,false,false,false,false,false,false] })
  }

  onCaseScanErrorCallback(){
    this.setState({ onCaseScanError: false })
  }

  handleLPNAutoFocusCallback(){
    this.setState({autoFocusOnLPN: false})
  }

  proceedBundleScan = (position: number, scan: string) => {
    this.focusNextBundle(position)
    const bundleScannedPositions = produce(
      this.state.bundleScannedPositions,
      scanned => {
        scanned.push(position)
      })
    const bundleCodes = produce(this.state.bundleCodes, bundleCodes => {
      bundleCodes.push(scan)
    })
    this.setState({ bundleScannedPositions, bundleCodes })
  }

  handleBundleScan (position: number, scan: string) {
    const { palletMeta, workOrderId } = this.state
    if (palletMeta) {
      const payload:PackTraceValidatePackCodeRequest = {}
      if(workOrderId) payload.workOrder = workOrderId
      if(scan) payload.packCode = scan
      packTraceApi.validatePackCode(payload, response => {
        if(!response) return
        if(response.valid) this.proceedBundleScan(position, scan)
      }, error => { 
        this.handleUPCValidationError(error, position, scan)
      })
    } else {
      this.proceedBundleScan(position, scan)
    }
  }

  handleUPCValidationError (error : any | null, position: number, scan: string) {
    const { response } = error
    const requestError = (response && response.data.message)
    const message = requestError || error.message

    if (error != null) {
      alertService.error(message)
      var isPopupOpen = false;
      if(message == "Customer part number does not match") {
        isPopupOpen = true;
      } 
        const { onScanError } = this.state
        onScanError[position] = true
        this.setState({ isPopupOpen: isPopupOpen, onScanError: onScanError })
        this.dumpSession()
    }
  }

  createPackTrace () {
    if (!this.canCreate) return
    const payload: PackTraceCreateRequest = {}
    const { lineNumber, workOrderId, palletCode, caseCode } = this.state
    const { palletMeta, bundleCodes, latitude, longitude } = this.state
    if (workOrderId) payload.workOrder = workOrderId
    if (lineNumber) payload.lineNumber = lineNumber
    if (palletMeta && palletCode) payload.pallet = palletCode
    if (caseCode) payload.case = caseCode
    if (bundleCodes) payload.bundles = bundleCodes
    if (latitude) payload.latitude = latitude
    if (longitude) payload.longitude = longitude
    packTraceApi.create(payload, packTrace => {
      if (!packTrace) return
      alertService.success('Success!')
      this.pushCaseScannedCodes(caseCode)
      this.dumpSession()
      this.reset()
    })
  }

  handleCaseScan (caseCode: string) {
    if (!caseCode) return
    const { palletMeta, caseScannedCodes } = this.state
    if (includes(caseScannedCodes, caseCode)) {
      this.setState({caseCode: "", onCaseScanError: true})
      alertService.warning('Duplicate scan')
      return
    }
    if (palletMeta) {
      this.setState({ caseCode }, this.createPackTrace)
    } else {
      inventoryCaseApi.readByCode(caseCode, inventoryCase => {
        if (!inventoryCase) return
        const { pallet } = inventoryCase
        const { sessionPallet: currentSessionPallet } = this.state
        const hasCaseScanned = size(caseScannedCodes)
        const sessionPallet = hasCaseScanned ? currentSessionPallet : pallet || null
        if (sessionPallet) {
          if (!pallet || (pallet && (pallet.id !== sessionPallet.id))) {
            alertService.info(`Case should be from ${sessionPallet.code}`)
            return
          }
        }
        this.setState({ caseCode, sessionPallet }, this.createPackTrace)
      })
    }
  }

  readGeolocation () {
    geolocationService.access(coordinate => {
      if (!coordinate) return
      const { latitude, longitude } = coordinate
      this.setState({ latitude, longitude })
      this.initialState.latitude = latitude
      this.initialState.longitude = longitude
    })
  }

  readPalletMetaExist () {
    metaApi.existByAddonName('extendPackTraceCreateFormWithPallet', response => {
      if (!response || !response.exist) return
      this.setState({ palletMeta: response.exist })
    })
  }

  submitManagerPassword = (managerUser: string, managerPassword: string) => {
    const payload:ManagerUpcRequest = {}
    payload.managerUser = managerUser
    payload.password = managerPassword
    packTraceApi.upcUnblock(payload, response => {
      if(!response) return
      if(response.valid) {
        this.setState({isPopupOpen: false})
        this.dumpSession()
      }
    })
  }

  componentDidMount () {
    this.readGeolocation()
    this.readPalletMetaExist()
    this.restoreSession()
  }

  render () {
    return (
      <ContentLayout>
        <ContentMain layout="full">
          <Card>
            <HeadingRow
              heading={i18n("Pack Trace")}
              doc='pack-traces-create-pack-trace'>
              <Button
                text={i18n("Reset")}
                onClick={this.handleResetClick} />
              <Button
                text={i18n("Start new session")}
                type="danger"
                onClick={this.handleStartNewSessionClick} />
            </HeadingRow>
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-x-5">
              <div className="col-span-2">
                <Row column={2}>
                  <SearchPicker
                    name="code"
                    value={this.state.code}
                    options={this.workOrderOptions}
                    label={i18n("Work order")}
                    onSearch={this.handleWorkOrderSearch}
                    onChange={this.handleInputChange}
                    onSelect={this.handleWorkOrderSelect}
                    onBlur={this.handleWorkOrderBlur} />
                  <InputText
                    name="lineNumber"
                    value={this.state.lineNumber}
                    label={i18n("Line number")}
                    onChange={this.handleInputChange} />
                    {this.palletCodeInput}
                </Row>
                {this.progress}
                <PackTraceCreateBundles
                  bundles={this.bundlesCount(this.state.workOrder)}
                  scannedPositions={this.state.bundleScannedPositions}
                  scannedCodes={this.state.bundleCodes}
                  inputRefs={this.bundleRefs}
                  onScan={this.handleBundleScan}
                  onScanError={this.state.onScanError}
                  onScanErrorCallback={this.onScanErrorCallback} />
                {this.case}
              </div>
              <div className="col-span-2 flex flex-col gap-4">
                {this.item}
                {this.palletCases}
              </div>
            </div>
          </Card>
        </ContentMain>
        <Popup isOpen={this.state.isPopupOpen} onSubmit={this.submitManagerPassword} />
      </ContentLayout>
    )
  }
}
